import { useMemo } from "react";
import { Link } from "react-router-dom";
import { V2, fmtBig, fmtChange, changeColor, ageMinutes } from "./tokens";
import { useV2Global, useV2History, useV2Movers, useV2MarketPulse, useV2LayerIndex, useV2LayerHistory, useV2FearGreed, useV2Stablecoins } from "./useV2Data";
import { SectionHead } from "@/components/home/shared";

/* MARKET INTELLIGENCE — EXACT IMPLEMENTATION AUTHORITY Figma 230:204
   ("DO NOT REDUCE STRUCTURE"). The APPROVED 3D TERRAIN is a locked production
   asset (/home2/terrain-market.jpg) — never recreated. Figma controls layout /
   hierarchy / module presence; live NewsLayer APIs control every value.
   Structure never collapses on missing data: unsupported values render
   truthful neutral states (—, Unavailable) without removing modules.
   Market Sentiment is the production NewsLayer Layer Index methodology
   (sentiment = bull/(bull+bear) signals from 24h headline classification,
   zone label from layer_index.py, daily history waveform). FEAR & GREED is
   the Alternative.me Crypto Fear & Greed Index (founder-approved provider —
   visible attribution required; NEVER blended with or derived from the
   Layer Index). STABLECOIN SUPPLY (founder ruling — replaces the former
   Global Liquidity cell, which must never return under that label) is total
   USD-pegged stablecoin circulating cap from DeFiLlama with real daily
   history. Both render truthful — / UNAVAILABLE states on provider failure. */

const MI = {
  /* Figma 230:204 canonical card strokes (1.5px gradient, exact live paints)
     + per-card glow accents (founder hover-glow sync with the Intel row) */
  cardBorder: {
    terrain: { grad: "linear-gradient(270deg, rgba(109,53,255,0) 0%, #412099 100%)", glow: "#6d35ff" },
    composition: { grad: "linear-gradient(180deg, #f59e0b 0%, rgba(143,92,6,0) 100%)", glow: "#f59e0b" },
    movers: { grad: "linear-gradient(0deg, #11cc8f 0%, rgba(9,102,71,0) 100%)", glow: "#11cc8f" },
    pulse: { grad: "linear-gradient(270deg, #6d35ff 0%, rgba(65,32,153,0) 100%)", glow: "#6d35ff" },
  },
  label: "#8b5cff", sub: "#8b93a7", line: "#22293a", text: "#f6f7fb",
  pillBg: "rgba(13,17,27,0.98)", pillBorder: "#394052",
  stripBg: "#080d18", stripBorder: "#22293a",
  btc: "#f59e0b", eth: "#3b82f6", others: "#8b5cff",
  btcMark: "#f7931a", ethMarkBg: "#1f263d", ethMarkFg: "#949ef2",
  mint: "#41e7b7", cyan: "#19d5ff", cyanUi: "#22d3ee", green: "#27e7a6",
  gaugeTrack: "#202738", needle: "#d8dce8",
};

const fmtT = (n?: number | null) =>
  n == null ? "—" : n >= 1e12 ? `$${(n / 1e12).toFixed(2)}T` : fmtBig(n);
const fmtDom = (n?: number | null) => (n == null ? "—" : `${n.toFixed(1)}%`);
const fmtPriceX = (n?: number | null) => {
  if (n == null) return "—";
  if (n >= 1000) return `$${Math.round(n).toLocaleString()}`;
  if (n >= 1) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(4)}`;
};

/* Canonical asset marks (230:489 identity rule: official marks, never generic dots) */
const BtcMark = ({ size }: { size: number }) => (
  <span aria-hidden className="rounded-full inline-flex items-center justify-center shrink-0"
    style={{ width: size, height: size, background: MI.btcMark }}>
    <span style={{ fontSize: size * 0.6, color: "#fff", fontWeight: 700, lineHeight: 1, transform: "rotate(12deg)" }}>₿</span>
  </span>
);
const EthMark = ({ size }: { size: number }) => (
  <span aria-hidden className="rounded-full inline-flex items-center justify-center shrink-0"
    style={{ width: size, height: size, background: MI.ethMarkBg }}>
    <svg width={size * 0.46} height={size * 0.7} viewBox="0 0 12 19" fill="none">
      <path d="M6 0L11.6 9.4L6 12.9L0.4 9.4L6 0Z" fill={MI.ethMarkFg} />
      <path d="M6 14L11.6 10.5L6 19L0.4 10.5L6 14Z" fill={MI.ethMarkFg} opacity="0.75" />
    </svg>
  </span>
);
const CoinLogo = ({ c, size }: { c: any; size: number }) =>
  c.image ? (
    <img src={c.image} alt="" aria-hidden loading="lazy"
      className="rounded-full shrink-0 object-cover" style={{ width: size, height: size }} />
  ) : (
    <span aria-hidden className="rounded-full inline-flex items-center justify-center shrink-0"
      style={{ width: size, height: size, background: "#294a8a", border: "1px solid rgba(74,82,102,0.72)" }}>
      <span className="nl-geist font-semibold" style={{ fontSize: size * 0.5, color: "#dfe4f2", lineHeight: 1 }}>
        {(c.symbol || "?").slice(0, 1)}
      </span>
    </span>
  );

/* Terrain beacons (230:208) — BTC / ETH / OTHERS slots always present */
type BeaconPos = { base: [number, number]; md: [number, number]; xl: [number, number] };
const BEACONS: Record<string, BeaconPos> = {
  btc: { base: [15.6, 20.2], md: [20.1, 23.9], xl: [21.7, 23.1] },
  eth: { base: [53.6, 14.4], md: [55.8, 11.3], xl: [57.1, 6.7] },
  others: { base: [73.2, 40.4], md: [81.4, 31.4], xl: [82.0, 30.7] },
};
const Beacon = ({ id, label, value, border, dot, mark }: {
  id: string; label: string; value: string; border: string; dot: string; mark?: React.ReactNode;
}) => {
  const p = BEACONS[id];
  return (
    <div data-testid={`v2-mi-beacon-${id}`} className="absolute mi-beacon" data-beacon={id}
      style={{ ["--bl-b" as any]: `${p.base[0]}%`, ["--bt-b" as any]: `${p.base[1]}%`,
               ["--bl-m" as any]: `${p.md[0]}%`, ["--bt-m" as any]: `${p.md[1]}%`,
               ["--bl-x" as any]: `${p.xl[0]}%`, ["--bt-x" as any]: `${p.xl[1]}%` }}>
      <div className="rounded-[10px] flex items-center gap-[10px] px-[9px] py-[6px] md:px-[12px] md:py-[8px]"
        style={{ background: "rgba(8,11,18,0.94)", border: `1px solid ${border}` }}>
        <div>
          <p className="nl-geist text-[9px] leading-[11px] md:text-[12px] md:leading-[16px]" style={{ color: MI.text }}>{label}</p>
          <p className="nl-mono text-[10px] leading-[13px] md:text-[15px] md:leading-[20px]" style={{ color: MI.text }}>{value}</p>
        </div>
        {mark}
      </div>
      <div className="hidden md:flex flex-col items-center absolute left-1/2 top-full -translate-x-1/2" aria-hidden>
        <span style={{ width: 1, height: 38, background: dot, opacity: 0.85 }} />
        <span className="rounded-full" style={{ width: 10, height: 10, background: dot }} />
      </div>
    </div>
  );
};

/* Real sparkline from recorded observations (88×32 + baseline per 230:403) */
const Spark = ({ vals, color, w = 88 }: { vals: number[]; color: string; w?: number }) => {
  if (vals.length < 2) return null;
  const h = 26;
  const min = Math.min(...vals), max = Math.max(...vals), span = max - min || 1;
  const pts = vals.map((v, i) =>
    `${((i / (vals.length - 1)) * w).toFixed(1)},${(h - 3 - ((v - min) / span) * (h - 6)).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h + 6} viewBox={`0 0 ${w} ${h + 6}`} aria-hidden className="shrink-0">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.7" strokeLinejoin="round" strokeLinecap="round" />
      <line x1="0" y1={h + 5} x2={w} y2={h + 5} stroke="#1b2230" strokeWidth="1" />
    </svg>
  );
};

/* Gauge arcs (230:276 / 230:481): mint → cyan → violet semicircle */
const arc = (cx: number, cy: number, r: number, a1: number, a2: number) => {
  const p = (a: number) => [cx + r * Math.cos((a * Math.PI) / 180), cy - r * Math.sin((a * Math.PI) / 180)];
  const [x1, y1] = p(a1), [x2, y2] = p(a2);
  return `M ${x1.toFixed(1)} ${y1.toFixed(1)} A ${r} ${r} 0 0 1 ${x2.toFixed(1)} ${y2.toFixed(1)}`;
};
const GAUGE_SEGS: [number, number, string][] = [[180, 112, MI.mint], [112, 72, MI.cyan], [72, 0, MI.others]];

const FGGauge = ({ value }: { value: number | null }) => {
  const a = value == null ? null : 180 - (Math.max(0, Math.min(100, value)) / 100) * 180;
  return (
    <svg width="60" height="32" viewBox="0 0 60 32" aria-hidden className="shrink-0">
      <path d={arc(30, 30, 24, 180, 0)} fill="none" stroke={MI.gaugeTrack} strokeWidth="7" />
      {a != null && GAUGE_SEGS.map(([a1, a2, c], i) => (
        <path key={i} d={arc(30, 30, 24, a1, a2)} fill="none" stroke={c} strokeWidth="7" />
      ))}
      {a != null && (
        <>
          <line x1="30" y1="30" x2={30 + 15 * Math.cos((a * Math.PI) / 180)} y2={30 - 15 * Math.sin((a * Math.PI) / 180)}
            stroke={MI.needle} strokeWidth="1.6" />
          <circle cx="30" cy="30" r="2.4" fill={MI.needle} />
        </>
      )}
    </svg>
  );
};

const SentimentGauge = ({ value }: { value: number | null }) => {
  const a = value == null ? null : 180 - (Math.max(0, Math.min(100, value)) / 100) * 180;
  return (
    <svg width="54" height="34" viewBox="0 0 54 34" aria-hidden className="shrink-0">
      <path d={arc(27, 29, 21, 180, 0)} fill="none" stroke="#1d2433" strokeWidth="6" />
      {GAUGE_SEGS.map(([a1, a2, c], i) => (
        <path key={i} d={arc(27, 29, 21, a1, a2)} fill="none" stroke={c} strokeWidth="6" />
      ))}
      {a != null && (
        <>
          <line x1="27" y1="29" x2={27 + 13 * Math.cos((a * Math.PI) / 180)} y2={29 - 13 * Math.sin((a * Math.PI) / 180)}
            stroke={MI.needle} strokeWidth="1.5" />
          <circle cx="27" cy="29" r="2.3" fill={MI.needle} />
        </>
      )}
    </svg>
  );
};

/* Composition donut (230:246) — real shares; unknown remainder stays neutral track */
const Donut = ({ btc, eth, others, cap }: { btc: number | null; eth: number | null; others: number | null; cap?: number | null }) => {
  const S = 140, r = 57.5, C = 2 * Math.PI * r;
  const segs = [
    { key: "btc", pct: btc, color: MI.btc }, { key: "eth", pct: eth, color: MI.eth },
    { key: "others", pct: others, color: MI.others },
  ].filter((s) => s.pct != null) as { key: string; pct: number; color: string }[];
  let acc = 0;
  return (
    <div className="relative shrink-0 w-[110px] h-[110px] md:w-[86px] md:h-[86px] xl:w-[92px] xl:h-[92px] min-[1440px]:w-[100px] min-[1440px]:h-[100px]"
      data-testid="v2-mi-donut" role="img"
      aria-label={`Market composition: BTC ${fmtDom(btc)}, ETH ${fmtDom(eth)}, Others ${fmtDom(others)}`}>
      <svg viewBox={`0 0 ${S} ${S}`} className="w-full h-full -rotate-90">
        <circle cx="70" cy="70" r="69" fill="none" stroke={MI.others} strokeOpacity="0.18" strokeWidth="2" />
        <circle cx="70" cy="70" r="63" fill="none" stroke={MI.cyan} strokeOpacity="0.13" strokeWidth="1" />
        <circle cx="70" cy="70" r={r} fill="none" stroke="#1a2130" strokeWidth="13" />
        {segs.map((s) => {
          const dash = (s.pct / 100) * C;
          const off = -(acc / 100) * C;
          acc += s.pct;
          return <circle key={s.key} cx="70" cy="70" r={r} fill="none" stroke={s.color} strokeWidth="13"
            strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={off} />;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center" aria-hidden>
        <p className="nl-geist text-[16px] md:text-[13px] xl:text-[17px] leading-[1.2]" style={{ color: MI.text }}>{fmtT(cap)}</p>
        <p className="nl-mono text-[8px] leading-[1.4] mt-[1px]" style={{ color: MI.sub }}>TOTAL CAP</p>
      </div>
    </div>
  );
};

const CardLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="nl-mono text-[11px] leading-[15px] md:text-[12px] md:leading-[16px] tracking-[0.02em] whitespace-nowrap" style={{ color: MI.label }}>
    {children}
  </p>
);

/* Pulse category label — real article categories only */
const PULSE_CATS: [string, string, string][] = [
  ["government-policy", "POLICY", MI.others],
  ["defi", "DEFI", MI.mint],
  ["security", "SECURITY", MI.btc],
  ["markets", "MARKETS", "#27e7b3"],
  ["crypto", "CRYPTO", MI.cyanUi],
];
const pulseCat = (cats: string[] = []) =>
  PULSE_CATS.find(([k]) => cats.includes(k)) || [null, "NEWS", MI.sub] as any;

const PulseGlyph = ({ color }: { color: string }) => (
  <svg width="28" height="18" viewBox="0 0 28 18" aria-hidden className="shrink-0 mt-[4px]">
    {[{ x: 2, y1: 8, y2: 15 }, { x: 8, y1: 4, y2: 7 }, { x: 14, y1: 9, y2: 18 }, { x: 20, y1: 1, y2: 6 }, { x: 26, y1: 6, y2: 14 }].map((l, i) => (
      <line key={i} x1={l.x} y1={l.y1} x2={l.x - 2} y2={l.y2} stroke={color} strokeWidth="1.4" opacity="0.85" />
    ))}
  </svg>
);

export const MarketIntelligenceField = () => {
  const { data: global } = useV2Global();
  const { data: hist } = useV2History(24, true);
  const { data: mv } = useV2Movers();
  const { data: pulse } = useV2MarketPulse();
  const { data: li } = useV2LayerIndex();
  const { data: liHist } = useV2LayerHistory(14);
  const { data: fg } = useV2FearGreed();
  const { data: sc } = useV2Stablecoins();

  const pts: any[] = hist?.points || [];
  const btc = global?.btc_dominance, eth = global?.eth_dominance;
  const others = btc != null && eth != null ? Math.max(0, 100 - btc - eth) : null;

  const movers = mv?.available
    ? [...(mv.gainers || []), ...(mv.losers || [])]
        .filter((c: any) => c.change_24h != null)
        .sort((a: any, b: any) => Math.abs(b.change_24h) - Math.abs(a.change_24h)).slice(0, 5)
    : [];

  const stories = (pulse?.items || []).slice(0, 5);
  const newestAge = stories.length ? ageMinutes(stories[0].published_at) : null;
  const pulseLive = newestAge != null && newestAge <= 15;

  /* Layer Index → Fear/Greed + Sentiment modules (real NewsLayer index) */
  const liValue: number | null = li?.value ?? null;
  const liLabel: string | null = li?.label ?? null;
  const bull = li?.components?.bull_signals, bear = li?.components?.bear_signals;
  const balance = bull != null && bear != null && bull + bear > 0
    ? (() => { const r = bull / (bull + bear); return r > 0.55 ? "Bull-leaning" : r < 0.45 ? "Bear-leaning" : "Balanced"; })()
    : null;
  const liSeries: number[] = (liHist?.days || []).map((d: any) => d.value).filter((v: any) => v != null);

  /* Fear & Greed — Alternative.me index (separate metric; never the Layer Index) */
  const fgValue: number | null = fg?.available ? fg.value : null;
  const fgLabel: string | null = fg?.available ? fg.label : null;
  const fgColor = fgLabel && /greed/i.test(fgLabel) ? "#27e7b3"
    : fgLabel && /fear/i.test(fgLabel) ? "#ff4d67" : MI.cyanUi;

  /* Stablecoin supply — DeFiLlama total USD-pegged circulating cap */
  const scTotal: number | null = sc?.total_usd ?? null;
  const scSpark: number[] = (sc?.history || []).slice(-14);

  /* Real 24h series + deltas from recorded history (≥18h-old anchor required) */
  const series = useMemo(() => {
    const pick = (f: (p: any) => number | null | undefined) =>
      pts.map(f).filter((v): v is number => v != null);
    const delta = (f: (p: any) => number | null | undefined, latest?: number | null) => {
      if (latest == null || !pts.length) return null;
      const anchor = pts.find((p) => f(p) != null);
      if (!anchor) return null;
      const ageH = (Date.now() - new Date(anchor.t).getTime()) / 3.6e6;
      if (ageH < 18) return null;
      return latest - (f(anchor) as number);
    };
    return {
      mcap: pick((p) => p.mcap),
      btcDom: pick((p) => p.btc_dominance), btcDelta: delta((p) => p.btc_dominance, btc),
      ethDom: pick((p) => p.eth_dominance), ethDelta: delta((p) => p.eth_dominance, eth),
    };
  }, [pts, btc, eth]);

  const fmtPP = (n: number | null) =>
    n == null ? null : `${n >= 0 ? "+" : "−"}${Math.abs(n).toFixed(2)}pp`;

  /* 230:403 — SIX fixed intelligence modules. Structure never collapses:
     missing values render truthful neutral states. Delta/sub colors follow
     the Figma module-accent color logic. */
  const railCells: {
    key: string; label: string; value: string; sub: string | null; subColor: string;
    spark?: number[]; sparkColor?: string; gauge?: boolean; gaugeValue?: number | null;
    attribution?: string; title?: string;
  }[] = [
    { key: "mcap", label: "MARKET CAP", value: fmtT(global?.total_market_cap_usd),
      sub: fmtChange(global?.market_cap_change_24h_pct) || null, subColor: MI.others,
      spark: series.mcap, sparkColor: MI.others },
    { key: "btc-dom", label: "BTC DOMINANCE", value: fmtDom(btc),
      sub: fmtPP(series.btcDelta), subColor: MI.btc, spark: series.btcDom, sparkColor: MI.btc },
    { key: "eth-dom", label: "ETH DOMINANCE", value: fmtDom(eth),
      sub: fmtPP(series.ethDelta), subColor: MI.eth, spark: series.ethDom, sparkColor: "#3a8dff" },
    { key: "fear-greed", label: "FEAR & GREED INDEX",
      value: fgValue != null ? String(fgValue) : "—",
      sub: fgLabel ? fgLabel.toUpperCase() : "UNAVAILABLE",
      subColor: fgLabel ? fgColor : MI.sub,
      gauge: true, gaugeValue: fgValue,
      attribution: fgValue != null ? "SOURCE: ALTERNATIVE.ME" : undefined,
      title: "Crypto Fear & Greed Index — Source: Alternative.me" },
    { key: "sentiment", label: "MARKET SENTIMENT", value: liLabel ?? "—",
      sub: balance, subColor: MI.cyanUi, spark: liSeries, sparkColor: MI.cyan },
    { key: "stablecoins", label: "STABLECOIN SUPPLY", value: fmtT(scTotal),
      sub: scTotal != null ? fmtChange(sc?.change_24h_pct) : "UNAVAILABLE",
      subColor: scTotal != null ? changeColor(sc?.change_24h_pct) : MI.sub,
      spark: scSpark, sparkColor: MI.mint,
      title: "Total USD-pegged stablecoin circulating cap — Source: DeFiLlama" },
  ];

  if (!global && !pts.length && !li) return null;

  const cardBase = "relative rounded-[18px] overflow-hidden intel-accent";
  const cardStyle = (c: { grad: string; glow: string }) => ({
    border: "1.5px solid transparent",
    background: `linear-gradient(#000000,#000000) padding-box, ${c.grad} border-box`,
    ["--intel-glow" as any]: `${c.glow}59`,
  });

  return (
    <section data-testid="v2-market-intelligence" className="mt-[56px]">
      <style>{`
        .mi-beacon{left:var(--bl-b);top:var(--bt-b)}
        @media(min-width:768px){.mi-beacon{left:var(--bl-m);top:var(--bt-m)}}
        @media(min-width:1280px){.mi-beacon{left:var(--bl-x);top:var(--bt-x)}}
      `}</style>

      {/* Header — shared SectionHead (site-wide branding sync, founder order 2026-08-16) */}
      <SectionHead label="Data + journalism" title="Market Intelligence"
        sub="Real-time market structure and the stories moving it."
        cta="Explore Markets" to="/markets" testid="v2-mi-explore-link" module="market_intelligence" />

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-[minmax(0,700fr)_minmax(0,310fr)_minmax(0,350fr)] gap-[14px] md:gap-[16px]">
        {/* ============ TOTAL MARKET CAP + 3D TERRAIN (230:208) ============ */}
        <div className={`${cardBase} md:col-span-3 xl:col-span-1 xl:col-start-1 xl:row-start-1 xl:row-span-2`}
          style={cardStyle(MI.cardBorder.terrain)} data-testid="v2-mi-terrain">
          <div className="px-[14px] pt-[14px] md:px-[24px] md:pt-[20px]">
            <div className="flex items-start justify-between gap-[12px]">
              <div>
                <CardLabel>TOTAL MARKET CAP</CardLabel>
                <div className="flex items-baseline gap-[14px] md:gap-[18px] flex-wrap mt-[6px] md:mt-[8px]">
                  <p className="nl-sg text-[31px] leading-[40px] md:text-[36px] md:leading-[46px] xl:text-[38px] xl:leading-[48px] font-medium"
                    style={{ color: MI.text }} data-testid="v2-mi-cap">
                    {fmtT(global?.total_market_cap_usd)}
                  </p>
                  <p className="nl-mono text-[12px] leading-[16px] md:text-[16px] md:leading-[21px]"
                    style={{ color: changeColor(global?.market_cap_change_24h_pct) }}>
                    {fmtChange(global?.market_cap_change_24h_pct)}
                    <span className="nl-mono text-[9px] md:text-[11px] ml-[10px]" style={{ color: MI.sub }}>24H</span>
                  </p>
                </div>
              </div>
              <span className="nl-mono rounded-[8px] px-[14px] py-[6px] text-[9px] md:text-[11px] leading-[14px] shrink-0 inline-flex items-center gap-[8px]"
                style={{ background: MI.pillBg, border: `1px solid ${MI.pillBorder}`, color: MI.sub }}>
                24H
                <svg width="10" height="6" viewBox="0 0 10 6" aria-hidden>
                  <path d="M1 1L5 5L9 1" fill="none" stroke="#9da5b7" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              </span>
            </div>
          </div>

          {/* LOCKED ASSET — approved 3D terrain artwork (230:222) */}
          <div className="relative mt-[10px] md:mt-[16px]">
            <img src="/home2/terrain-market.jpg" alt=""
              className="w-full h-[208px] md:h-[318px] xl:h-[342px] object-cover block"
              data-testid="v2-mi-terrain-art" />
            <Beacon id="btc" label="BTC" value={fmtDom(btc)} border="rgba(255,163,24,0.85)" dot={MI.btc}
              mark={<span className="hidden md:inline-flex"><BtcMark size={24} /></span>} />
            <Beacon id="eth" label="ETH" value={fmtDom(eth)} border="rgba(58,141,255,0.85)" dot={MI.eth}
              mark={<span className="hidden md:inline-flex"><EthMark size={24} /></span>} />
            <Beacon id="others" label="OTHERS" value={fmtDom(others)} border="rgba(139,92,255,0.85)" dot={MI.others} />
          </div>

          {/* Intraday stats strip (230:234) — real observed range + volume */}
          <div className="mx-[10px] md:mx-[16px] my-[8px] md:my-[10px] xl:mt-[8px] xl:mb-[16px] rounded-[12px] grid grid-cols-3"
            style={{ background: MI.stripBg, border: `1px solid ${MI.stripBorder}` }} data-testid="v2-mi-range-strip">
            {[
              { l: "INTRADAY LOW", v: fmtT(hist?.low) },
              { l: "INTRADAY HIGH", v: fmtT(hist?.high) },
              { l: "24H VOLUME", v: fmtBig(global?.volume_24h_usd) },
            ].map((c, i) => (
              <div key={c.l} className={`px-[12px] md:px-[18px] py-[7px] md:py-[10px] ${i > 0 ? "border-l" : ""}`}
                style={{ borderColor: MI.stripBorder }}>
                <p className="nl-mono text-[8px] md:text-[9px] xl:text-[10px] leading-[13px]" style={{ color: MI.sub }}>{c.l}</p>
                <p className="nl-mono text-[14px] md:text-[15px] xl:text-[18px] leading-[20px] md:leading-[23px] mt-[1px]" style={{ color: MI.text }}>{c.v}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ============ MARKET COMPOSITION (230:246) — BTC / ETH / Others + sentiment gauge ============ */}
        <div className={`${cardBase} xl:col-start-2 xl:row-start-1`} style={cardStyle(MI.cardBorder.composition)} data-testid="v2-mi-composition">
          <div className="p-[16px] md:p-[18px] xl:p-[20px]">
            <CardLabel>MARKET COMPOSITION</CardLabel>
            <div className="flex items-center gap-[16px] md:gap-[12px] xl:gap-[14px] mt-[14px] xl:mt-[16px]">
              <Donut btc={btc ?? null} eth={eth ?? null} others={others} cap={global?.total_market_cap_usd} />
              <div className="min-w-0 flex-1 space-y-[12px] xl:space-y-[14px]">
                <div className="flex items-center gap-[8px]" data-testid="v2-mi-comp-btc">
                  <BtcMark size={12} />
                  <span className="nl-geist text-[12px] leading-[16px] truncate" style={{ color: MI.text }}>
                    <span className="hidden xl:inline">Bitcoin (BTC)</span><span className="xl:hidden">BTC</span>
                  </span>
                  <span className="nl-mono text-[12px] leading-[16px] ml-auto" style={{ color: MI.text }}>{fmtDom(btc)}</span>
                </div>
                <div className="flex items-center gap-[8px]" data-testid="v2-mi-comp-eth">
                  <EthMark size={12} />
                  <span className="nl-geist text-[12px] leading-[16px] truncate" style={{ color: MI.text }}>
                    <span className="hidden xl:inline">Ethereum (ETH)</span><span className="xl:hidden">ETH</span>
                  </span>
                  <span className="nl-mono text-[12px] leading-[16px] ml-auto" style={{ color: MI.text }}>{fmtDom(eth)}</span>
                </div>
                <div className="flex items-center gap-[8px]" data-testid="v2-mi-comp-others">
                  <span aria-hidden className="rounded-full shrink-0" style={{ width: 8, height: 8, background: MI.others, margin: "0 2px" }} />
                  <span className="nl-geist text-[12px] leading-[16px]" style={{ color: MI.text }}>Others</span>
                  <span className="nl-mono text-[12px] leading-[16px] ml-auto" style={{ color: MI.text }}>{fmtDom(others)}</span>
                </div>
              </div>
            </div>
            {/* Market Sentiment readout (230:261-272) — real NewsLayer Layer Index */}
            <div className="h-px mt-[14px] xl:mt-[16px]" style={{ background: MI.line }} aria-hidden />
            <div className="flex items-center gap-[10px] mt-[10px]" data-testid="v2-mi-sentiment">
              <p className="nl-mono text-[10px] leading-[13px] whitespace-nowrap" style={{ color: MI.sub }}>MARKET SENTIMENT</p>
              <span className="ml-auto"><SentimentGauge value={liValue} /></span>
              <p className="nl-sg text-[19px] leading-[24px] font-medium" style={{ color: MI.text }} data-testid="v2-mi-sentiment-value">
                {liValue != null ? liValue : "—"}
              </p>
              <p className="nl-geist text-[11px] leading-[14px]" style={{ color: MI.sub }}>{liLabel ?? "Unavailable"}</p>
            </div>
          </div>
        </div>

        {/* ============ TOP MOVERS (230:280) ============ */}
        <div className={`${cardBase} xl:col-start-2 xl:row-start-2`} style={cardStyle(MI.cardBorder.movers)} data-testid="v2-mi-movers">
          <div className="p-[16px] md:p-[18px]">
            <div className="flex items-center justify-between gap-[10px]">
              <CardLabel>TOP MOVERS · 24H</CardLabel>
              <Link to="/markets" data-testid="v2-mi-movers-viewall"
                className="hidden xl:inline-block nl-mono rounded-[8px] px-[10px] py-[6px] text-[11px] leading-[14px] whitespace-nowrap hover:opacity-80 transition-opacity"
                style={{ background: MI.pillBg, border: `1px solid ${MI.pillBorder}`, color: MI.sub }}>
                View all
              </Link>
            </div>
            {movers.length === 0 && (
              <p className="nl-geist text-[12px] leading-[16px] mt-[14px]" style={{ color: MI.sub }} data-testid="v2-mi-movers-unavailable">
                Mover data unavailable.
              </p>
            )}
            {/* 390: 2x2 field · 768+: ranked rows */}
            <div className="grid grid-cols-2 gap-x-[16px] gap-y-[14px] mt-[16px] md:hidden">
              {movers.slice(0, 4).map((c: any) => (
                <div key={c.symbol} className="flex items-center gap-[8px] min-w-0" data-testid={`v2-mi-mover-${c.symbol}`}>
                  <CoinLogo c={c} size={22} />
                  <span className="nl-geist text-[12px] leading-[16px] truncate" style={{ color: MI.text }}>{c.name || c.symbol}</span>
                  <span className="nl-mono text-[11px] leading-[14px] ml-auto shrink-0" style={{ color: changeColor(c.change_24h) }}>
                    {fmtChange(c.change_24h)}
                  </span>
                </div>
              ))}
            </div>
            <div className="hidden md:block mt-[4px]">
              {movers.map((c: any, i: number) => (
                <div key={c.symbol} data-testid={`v2-mi-mover-row-${c.symbol}`}
                  className={`flex items-center gap-[8px] py-[9px] ${i > 0 ? "border-t" : ""}`}
                  style={{ borderColor: "rgba(34,41,58,0.8)" }}>
                  <span className="nl-mono hidden xl:block text-[11px] leading-[14px] w-[8px] shrink-0" style={{ color: MI.sub }}>{i + 1}</span>
                  <CoinLogo c={c} size={22} />
                  <span className="nl-geist text-[12px] leading-[16px] truncate flex-1" style={{ color: MI.text }}
                    title={`${c.name || c.symbol} · R${c.rank}${c.volume_24h != null ? ` · ${fmtBig(c.volume_24h)} VOL` : ""}`}>
                    {c.name || c.symbol}
                  </span>
                  <span className="nl-mono hidden xl:block text-[10px] leading-[13px] shrink-0 w-[32px]" style={{ color: MI.sub }}>{c.symbol}</span>
                  <span className="nl-mono text-[11px] leading-[14px] shrink-0 w-[52px] text-right" style={{ color: MI.text }}>{fmtPriceX(c.price)}</span>
                  <span className="nl-mono text-[11px] leading-[14px] shrink-0 w-[44px] text-right"
                    style={{ color: changeColor(c.change_24h) }}>
                    {fmtChange(c.change_24h)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ============ MARKET PULSE (230:354) ============ */}
        <div className={`${cardBase} xl:col-start-3 xl:row-start-1 xl:row-span-2 flex flex-col`} style={cardStyle(MI.cardBorder.pulse)} data-testid="v2-mi-pulse">
          <div className="p-[16px] md:p-[18px] xl:p-[20px] flex flex-col flex-1">
            <div className="flex items-center justify-between gap-[10px]">
              <CardLabel>MARKET PULSE</CardLabel>
              {pulseLive ? (
                <span className="nl-mono rounded-[8px] px-[8px] py-[5px] text-[10px] leading-[13px]"
                  style={{ background: "rgba(6,37,29,0.86)", border: "1px solid rgba(24,201,139,0.58)", color: "#27e7b3" }}
                  data-testid="v2-mi-pulse-live">
                  ● LIVE
                </span>
              ) : (
                <span className="nl-mono rounded-[8px] px-[8px] py-[5px] text-[10px] leading-[13px]"
                  style={{ background: "rgba(13,17,27,0.9)", border: `1px solid ${MI.pillBorder}`, color: MI.sub }}
                  data-testid="v2-mi-pulse-age">
                  {newestAge != null ? `${newestAge >= 60 ? `${Math.floor(newestAge / 60)}h` : `${newestAge}m`} AGO` : "—"}
                </span>
              )}
            </div>
            {stories.length === 0 && (
              <p className="nl-geist text-[12px] leading-[16px] mt-[14px]" style={{ color: MI.sub }} data-testid="v2-mi-pulse-unavailable">
                No market stories available.
              </p>
            )}
            <div className="mt-[6px] flex-1">
              {stories.map((a: any, i: number) => {
                const [, catLabel, catColor] = pulseCat(a.categories);
                const glyphColor = [MI.cyanUi, MI.others, "#27e7b3", MI.btc, MI.cyanUi][i % 5];
                return (
                  <div key={a.slug} className={`${i >= 2 ? "hidden md:block" : ""} ${i >= 3 ? "md:hidden xl:block" : ""}`}>
                    <div className={`flex gap-[10px] py-[12px] xl:py-[13px] ${i > 0 ? "border-t" : ""}`}
                      style={{ borderColor: "rgba(34,41,58,0.8)" }} data-testid={`v2-mi-pulse-item-${i}`}>
                      <span className="nl-mono text-[10px] leading-[13px] w-[34px] shrink-0 pt-[1px]" style={{ color: MI.sub }}>
                        {(() => { const m = ageMinutes(a.published_at); return m == null ? "" : m < 60 ? `${m}m` : m < 1440 ? `${Math.floor(m / 60)}h` : `${Math.floor(m / 1440)}d`; })()}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="nl-mono text-[10px] leading-[13px]" style={{ color: catColor }}>{catLabel}</p>
                        <Link to={`/article/${a.slug}`} className="block mt-[5px] hover:opacity-80 transition-opacity"
                          data-testid={`v2-mi-pulse-link-${i}`}>
                          <p className="nl-geist text-[12px] leading-[16px] line-clamp-2 xl:line-clamp-3" style={{ color: MI.text }}>
                            {a.title}
                          </p>
                        </Link>
                      </div>
                      <PulseGlyph color={glyphColor} />
                    </div>
                  </div>
                );
              })}
            </div>
            <Link to="/markets" data-testid="v2-mi-pulse-viewall"
              className="nl-geist text-[13px] leading-[17px] mt-[10px] hover:opacity-80 transition-opacity"
              style={{ color: MI.cyanUi }}>
              View all market news →
            </Link>
          </div>
        </div>
      </div>

      {/* ============ BOTTOM MARKET INTELLIGENCE RAIL (230:403 — six fixed modules, all breakpoints) ============ */}
      <div className="relative rounded-[18px] overflow-hidden mt-[14px] md:mt-[16px]"
        style={{
          border: "2px solid transparent",
          background: "linear-gradient(#000000,#000000) padding-box, linear-gradient(to top, #22d3ee 0%, rgba(102,102,102,0) 100%) border-box",
        }} data-testid="v2-mi-rail">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
          {railCells.map((c, i) => {
            const viz = (w: number) => c.gauge
              ? <FGGauge value={c.gaugeValue ?? null} />
              : c.spark && c.spark.length >= 2 ? <Spark vals={c.spark} color={c.sparkColor!} w={w} /> : null;
            const dividers = [
              i % 2 === 0 ? "border-r" : "",
              i < 4 ? "border-b" : "",
              i % 3 !== 2 ? "md:border-r" : "md:border-r-0",
              i < 3 ? "md:border-b" : "md:border-b-0",
              "xl:border-r-0 xl:border-b-0",
            ].join(" ");
            return (
              <div key={c.key}
                className={`min-w-0 p-[14px] md:p-[16px] xl:pl-[18px] xl:pr-[10px] xl:py-[16px] flex items-center justify-between gap-[8px] ${dividers}`}
                style={{ borderColor: "rgba(34,41,58,0.8)" }}
                data-testid={`v2-mi-rail-${c.key}`} title={c.title}>
                <div className="min-w-0 flex-1">
                  <p className="nl-mono text-[9px] md:text-[10px] leading-[13px] whitespace-nowrap" style={{ color: MI.sub }}>{c.label}</p>
                  <div className="flex items-center justify-between gap-[10px]">
                    <div className="min-w-0">
                      <p className="nl-sg text-[17px] leading-[22px] md:text-[20px] md:leading-[26px] font-medium mt-[4px] whitespace-nowrap" style={{ color: MI.text }}>{c.value}</p>
                      <p className="nl-mono text-[9px] md:text-[10px] leading-[13px] mt-[2px] whitespace-nowrap" style={{ color: c.subColor }}>
                        {c.sub ?? "\u00a0"}
                      </p>
                      {c.attribution && (
                        <p className="nl-mono text-[8px] leading-[10px] mt-[3px] whitespace-nowrap" style={{ color: "#5c6478" }}
                          data-testid={`v2-mi-rail-${c.key}-attribution`}>
                          {c.attribution}
                        </p>
                      )}
                    </div>
                    <span className="xl:hidden shrink-0">{viz(60)}</span>
                  </div>
                </div>
                <span className="hidden xl:block shrink-0">{viz(88)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
